import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');

class WhatsappVoiceClient extends EventTarget {
  constructor() {
    super();
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.audioElement = null;
    this.active = false;
  }

  async initializeDevice() {
    // For WhatsApp, we don't have a long-lived 'device' like Twilio.
    // Initialization is just ensuring browser support and cleaning up.
    this.destroyDevice();
    this.initialized = true;
    return true;
  }

  get hasActiveConnection() {
    return this.active;
  }

  endClientCall() {
    this.active = false;
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.srcObject = null;
      this.audioElement.remove();
      this.audioElement = null;
    }
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.dispatchEvent(createCallDisconnectedEvent());
  }

  destroyDevice() {
    this.endClientCall();
    this.initialized = false;
  }

  async createOffer() {
    this.initialized = true;
    try {
      await this.setupPeerConnection();

      // Create offer
      let offer = await this.peerConnection.createOffer();

      // Enforce Opus 48kHz, 20ms ptime requirement
      let modifiedSdp = this.enforceOpusRequirements(offer.sdp);
      await this.peerConnection.setLocalDescription({
        type: 'offer',
        sdp: modifiedSdp,
      });

      // Wait for ICE candidates to finish gathering (ICE-FULL)
      await this.waitForIceGathering();

      const finalLocalSdp = this.peerConnection.localDescription.sdp;

      return finalLocalSdp;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create WhatsApp call offer:', error);
      this.endClientCall();

      // Re-throw with a user-friendly message for microphone permission errors
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        throw new Error('MICROPHONE_PERMISSION_DENIED');
      }
      if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        throw new Error('MICROPHONE_NOT_FOUND');
      }

      return null;
    }
  }

  async applyRemoteAnswer(sdpAnswer) {
    if (!this.peerConnection) {
      // eslint-disable-next-line no-console
      console.error(
        '[WhatsApp Call] No peer connection to apply remote answer'
      );
      return false;
    }

    try {
      const answer = new RTCSessionDescription({
        type: 'answer',
        sdp: sdpAnswer,
      });
      await this.peerConnection.setRemoteDescription(answer);
      this.active = true;

      // eslint-disable-next-line no-console
      console.log('[WhatsApp Call] Remote SDP answer applied successfully', {
        connectionState: this.peerConnection.connectionState,
        iceConnectionState: this.peerConnection.iceConnectionState,
        hasLocalStream: !!this.localStream,
        localTracks: this.localStream?.getTracks().length,
      });

      // Ensure audio element is playing (browsers may block autoplay)
      if (this.audioElement) {
        this.audioElement.play().catch(() => {});
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[WhatsApp Call] Failed to apply remote SDP answer:',
        error
      );
      return false;
    }
  }

  async setupPeerConnection() {
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    this.peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });

    this.peerConnection.ontrack = event => {
      // eslint-disable-next-line no-console
      console.log('[WhatsApp Call] Remote track received', {
        kind: event.track.kind,
        streams: event.streams.length,
      });
      if (!this.audioElement) {
        this.audioElement = document.createElement('audio');
        this.audioElement.autoplay = true;
        document.body.appendChild(this.audioElement);
      }
      this.remoteStream = event.streams[0];
      this.audioElement.srcObject = this.remoteStream;
      this.audioElement.play().catch(() => {});
    };

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection.connectionState;
      // eslint-disable-next-line no-console
      console.log(`[WhatsApp Call] Connection state: ${state}`);
      if (
        state === 'disconnected' ||
        state === 'failed' ||
        state === 'closed'
      ) {
        this.endClientCall();
      }
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      // eslint-disable-next-line no-console
      console.log(
        `[WhatsApp Call] ICE state: ${this.peerConnection.iceConnectionState}`
      );
    };
  }

  waitForIceGathering(timeoutMs = 3000) {
    return new Promise(resolve => {
      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      let timeoutId = null;

      const onGatheringChange = () => {
        if (this.peerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeoutId);
          this.peerConnection.removeEventListener(
            'icegatheringstatechange',
            onGatheringChange
          );
          resolve();
        }
      };

      // Proceed after timeout with whatever candidates we have
      timeoutId = setTimeout(() => {
        this.peerConnection.removeEventListener(
          'icegatheringstatechange',
          onGatheringChange
        );
        resolve();
      }, timeoutMs);

      this.peerConnection.addEventListener(
        'icegatheringstatechange',
        onGatheringChange
      );
    });
  }

  async joinClientCall({ conversationId, inboxId, callSid, webrtcPayload }) {
    if (!this.initialized) return null;

    try {
      await this.setupPeerConnection();

      // Set remote description from Meta's SDP offer
      const offer = new RTCSessionDescription({
        type: 'offer',
        sdp: webrtcPayload,
      });
      await this.peerConnection.setRemoteDescription(offer);

      // Create answer
      let answer = await this.peerConnection.createAnswer();

      // Enforce Opus 48kHz, 20ms ptime requirement
      let modifiedSdp = this.enforceOpusRequirements(answer.sdp);
      await this.peerConnection.setLocalDescription({
        type: 'answer',
        sdp: modifiedSdp,
      });

      // Wait for ICE candidates to finish gathering (ICE-FULL)
      await this.waitForIceGathering();

      const finalLocalSdp = this.peerConnection.localDescription.sdp;

      // Send the SDP answer to the backend API
      await VoiceAPI.answerWhatsappCall({
        conversationId,
        inboxId,
        callSid,
        sdp: finalLocalSdp,
      });

      this.active = true;
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to join WhatsApp call:', error);
      this.endClientCall();
      return null;
    }
  }

  async rejectCall({ inboxId, callSid }) {
    try {
      await VoiceAPI.rejectWhatsappCall({ inboxId, callSid });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to reject WhatsApp call:', error);
    }
    this.endClientCall();
  }

  async terminateCall({ inboxId, callSid }) {
    try {
      await VoiceAPI.terminateWhatsappCall({ inboxId, callSid });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to terminate WhatsApp call:', error);
    }
    this.endClientCall();
  }

  // eslint-disable-next-line class-methods-use-this
  enforceOpusRequirements(sdp) {
    // Force Opus codec to 48kHz and set ptime=20
    const lines = sdp.split('\r\n');

    // Find the Opus payload type
    const opusLine = lines.find(
      line => line.startsWith('a=rtpmap:') && line.includes('opus/48000')
    );
    const opusPayloadType = opusLine
      ? opusLine.split(':')[1].split(' ')[0]
      : null;

    if (opusPayloadType) {
      // Check if fmtp exists and modify/add it
      const fmtpIndex = lines.findIndex(line =>
        line.startsWith(`a=fmtp:${opusPayloadType}`)
      );
      if (fmtpIndex !== -1) {
        if (!lines[fmtpIndex].includes('ptime=20')) {
          lines[fmtpIndex] += '; ptime=20';
        }
      } else {
        // Find the rtpmap line index to insert fmtp right after
        const rtpmapIndex = lines.findIndex(line =>
          line.startsWith(`a=rtpmap:${opusPayloadType}`)
        );
        lines.splice(rtpmapIndex + 1, 0, `a=fmtp:${opusPayloadType} ptime=20`);
      }
    }

    return lines.join('\r\n');
  }
}

export default new WhatsappVoiceClient();
